// src/components/navbar/AdminNavbar.js
// Enhanced Admin Navbar with REAL-TIME WebSocket notifications, GLOBAL SEARCH, breadcrumbs, and modern UI
//
// --- UPDATE (toast once) ---------------------------------------------------------
//   • ONE notification bell: the FlowSense Notify bell (unread count, red pulse for critical /
//     needs-acknowledgement, opens the notification centre with read / ack / snooze / archive).
//     The navbar used to show two bells (this one and NavbarLinksAdmin's) with different lists.
//   • defaultProps → parameter defaults (React 18.3 warns about defaultProps on functions).
//   • Unused imports / state removed.
// --- END UPDATE ------------------------------------------------------------------

import {
    Box, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Flex, Link, Text,
    useColorModeValue, Icon, HStack, Tooltip, IconButton, Button, useBreakpointValue
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { MdSearch, MdChevronRight, MdExpandMore } from 'react-icons/md';
import AdminNavbarLinks from './NavbarLinksAdmin';
import NotificationBell from '../notify/NotificationBell'; // --- UPDATE: the one bell (FlowSense Notify)

// =========================================================================
// NEW: Import Global Search Component
// =========================================================================
import { GlobalSearch } from '../search/GlobalSearch';

export default function AdminNavbar(props) {
    const [scrolled, setScrolled] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [breadcrumbExpanded, setBreadcrumbExpanded] = useState(false);
    const isMobile = useBreakpointValue({ base: true, md: false });

    const {
        secondary = false,
        message,
        brandText = 'Dashboard',
        fixed = false,
        onOpen,
        isOpen,
        onClose,
        logoText,
        breadcrumbs = [],
        showNotifications = true,
        showSearch: showSearchProp = true,
        ...rest
    } = props;

    // Handle scroll effect
    useEffect(() => {
        const changeNavbar = () => {
            const isScrolled = window.scrollY > 1;
            setScrolled(isScrolled);
        };
        changeNavbar();
        window.addEventListener('scroll', changeNavbar, { passive: true });
        return () => window.removeEventListener('scroll', changeNavbar);
    }, []);

    // Style variables
    const mainText = useColorModeValue('navy.700', 'white');
    const secondaryText = useColorModeValue('gray.700', 'white');
    const mutedColor = useColorModeValue('gray.600', 'gray.400');
    const navbarPosition = fixed ? 'fixed' : 'absolute';
    const navbarFilter = 'none';
    const navbarBackdrop = 'blur(20px)';
    const navbarShadow = scrolled ? 'md' : 'none';
    const navbarBg = useColorModeValue(
        scrolled ? 'rgba(244, 247, 254, 0.9)' : 'rgba(244, 247, 254, 0.2)', 
        scrolled ? 'rgba(11,20,55,0.9)' : 'rgba(11,20,55,0.5)'
    );
    const navbarBorder = 'transparent';
    const secondaryMargin = '0px';
    const paddingX = '15px';
    const gap = '0px';

    const defaultBreadcrumbs = [
        { label: 'Pages', href: '#' },
        { label: brandText, href: '#', isCurrentPage: true }
    ];

    const breadcrumbsToUse = breadcrumbs.length > 0 ? breadcrumbs : defaultBreadcrumbs;
    const shouldTruncate = breadcrumbsToUse.length > 3 && !breadcrumbExpanded;

    return (
        <Box
            position={navbarPosition}
            boxShadow={navbarShadow}
            bg={navbarBg}
            borderColor={navbarBorder}
            filter={navbarFilter}
            backdropFilter={navbarBackdrop}
            backgroundPosition='center'
            backgroundSize='cover'
            borderRadius='16px'
            borderWidth='1.5px'
            borderStyle='solid'
            transitionDelay='0s, 0s, 0s, 0s'
            transitionDuration='0.25s, 0.25s, 0.25s, 0s'
            transitionProperty='box-shadow, background-color, filter, border'
            transitionTimingFunction='linear, linear, linear, linear'
            alignItems={{ xl: 'center' }}
            display={secondary ? 'block' : 'flex'}
            minH='75px'
            justifyContent={{ xl: 'center' }}
            lineHeight='25.6px'
            mx='auto'
            mt={secondaryMargin}
            pb='8px'
            right={{ base: '12px', md: '30px', lg: '30px', xl: '30px' }}
            px={{ sm: paddingX, md: '10px' }}
            ps={{ xl: '12px' }}
            pt='8px'
            top={{ base: '12px', md: '16px', lg: '20px', xl: '20px' }}
            w={{ 
                base: 'calc(100vw - 6%)', 
                md: 'calc(100vw - 8%)', 
                lg: 'calc(100vw - 6%)', 
                xl: 'calc(100vw - 300px - 60px)', 
                '2xl': 'calc(100vw - 300px - 75px)' 
            }}
            zIndex={1100}
            {...rest}
        >
            <Flex
                w='100%'
                flexDirection={{ sm: 'column', md: 'row' }}
                alignItems={{ xl: 'center' }}
                mb={gap}
            >
                {/* Left side: Breadcrumbs and Brand */}
                <Box mb={{ sm: '8px', md: '0px' }} flex="1" minW="0">
                    <Breadcrumb 
                        separator={<Icon as={MdChevronRight} color={mutedColor} />}
                        fontSize="sm"
                        mb={2}
                    >
                        {shouldTruncate ? (
                            <>
                                <BreadcrumbItem color={secondaryText}>
                                    <BreadcrumbLink href={breadcrumbsToUse[0].href}>
                                        {breadcrumbsToUse[0].label}
                                    </BreadcrumbLink>
                                </BreadcrumbItem>
                                
                                <BreadcrumbItem>
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        onClick={() => setBreadcrumbExpanded(!breadcrumbExpanded)}
                                        leftIcon={<Icon as={MdExpandMore} />}
                                    >
                                        ...
                                    </Button>
                                </BreadcrumbItem>
                                
                                <BreadcrumbItem 
                                    color={mainText} 
                                    isCurrentPage
                                    fontWeight="600"
                                >
                                    <BreadcrumbLink href="#">
                                        {breadcrumbsToUse[breadcrumbsToUse.length - 1].label}
                                    </BreadcrumbLink>
                                </BreadcrumbItem>
                            </>
                        ) : (
                            breadcrumbsToUse.map((crumb, index) => (
                                <BreadcrumbItem 
                                    key={index}
                                    color={crumb.isCurrentPage ? mainText : secondaryText}
                                    isCurrentPage={crumb.isCurrentPage}
                                    fontWeight={crumb.isCurrentPage ? '600' : '400'}
                                >
                                    <BreadcrumbLink href={crumb.href || '#'}>
                                        {crumb.icon && <Icon as={crumb.icon} mr={1} />}
                                        {crumb.label}
                                    </BreadcrumbLink>
                                </BreadcrumbItem>
                            ))
                        )}
                    </Breadcrumb>
                    
                    <Link 
                        color={mainText} 
                        href='#' 
                        bg='inherit' 
                        borderRadius='inherit' 
                        fontWeight='bold' 
                        fontSize={{ base: '24px', md: '28px', xl: '34px' }}
                        _hover={{ color: mainText, textDecoration: 'none' }} 
                        _active={{ 
                            bg: 'inherit', 
                            transform: 'none', 
                            borderColor: 'transparent' 
                        }} 
                        _focus={{ boxShadow: 'none' }}
                        transition="all 0.2s"
                        display="block"
                        noOfLines={1}
                    >
                        {brandText}
                    </Link>
                </Box>
                
                {/* Right side: Search, Notifications, Links */}
                <Box ms='auto' w={{ sm: '100%', md: 'unset' }}>
                    <HStack spacing={{ base: 2, md: 3 }} justify="flex-end">
                        {/* =========================================================
                            NEW: GLOBAL SEARCH MOUNTED HERE
                            ========================================================= */}
                        {showSearchProp && (
                            <>
                                {isMobile ? (
                                    <Tooltip label="Search">
                                        <IconButton
                                            icon={<MdSearch />}
                                            variant="ghost"
                                            onClick={() => setShowSearch(!showSearch)}
                                            aria-label="Search"
                                        />
                                    </Tooltip>
                                ) : (
                                    <GlobalSearch isMobile={false} />
                                )}
                            </>
                        )}
                        
                        {/* --- UPDATE: Notifications — the single FlowSense Notify bell */}
                        {showNotifications && <NotificationBell />}

                        {/* Admin Navbar Links */}
                        <AdminNavbarLinks
                            onOpen={onOpen}
                            secondary={secondary}
                            fixed={fixed}
                            scrolled={scrolled}
                            isOpen={isOpen}
                            onClose={onClose}
                            logoText={logoText}
                        />
                    </HStack>
                </Box>
            </Flex>
            
            {/* Mobile Search rendering via the GlobalSearch component */}
            {isMobile && showSearchProp && (
                <GlobalSearch isMobile={true} showSearch={showSearch} setShowSearch={setShowSearch} />
            )}
            
            {/* Optional secondary message */}
            {secondary && message && (
                <Text color='white' mt={2}>{message}</Text>
            )}
        </Box>
    );
}

AdminNavbar.propTypes = {
    brandText: PropTypes.string,
    variant: PropTypes.string,
    secondary: PropTypes.bool,
    fixed: PropTypes.bool,
    onOpen: PropTypes.func,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    message: PropTypes.string,
    logoText: PropTypes.string,
    breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        href: PropTypes.string,
        icon: PropTypes.elementType,
        isCurrentPage: PropTypes.bool
    })),
    showNotifications: PropTypes.bool,
    showSearch: PropTypes.bool
};
